import React, { useEffect } from "react";
import { Formik, FormikProps, Form } from "formik";
import Button, { ButtonThemes } from "ui-components/button/button";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import LabeledDateInput from "ui-components/form/date/xgsFormDate";
import LabeledInput from "ui-components/molecules/labeled-inputs/labeledInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import XGSRadio from "ui-components/xgs-radio/xgs-radio";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import moment from "moment-timezone";
import "./arriveLoadModal.scss";

interface ArriveLoadFormValues {
  arrivalDate: string;
  arrivalTime: string;
  seal: string;
  checkedBy: string;
  sealCondition: string;
  reasonForDamagedSeal: string;
}

interface ArriveLoadModalProps {
  isOpen: boolean;
  onClose: () => void;
  manifestNumber?: string;
  trailerNumber?: string;
  originTerminal?: string;
  freightType?: string;
  originCity?: string;
  destinationTerminalName?: string;
  destinationTerminalTimezone?: string;
  onConfirm: (values: ArriveLoadFormValues, isLocal: boolean) => void;
}

const initialValues: ArriveLoadFormValues = {
  arrivalDate: "",
  arrivalTime: "",
  seal: "",
  checkedBy: "",
  sealCondition: "",
  reasonForDamagedSeal: "",
};

const ArriveLoadModal: React.FC<ArriveLoadModalProps> = ({
  isOpen,
  onClose,
  manifestNumber,
  trailerNumber,
  originTerminal,
  freightType,
  originCity,
  destinationTerminalName,
  destinationTerminalTimezone,
  onConfirm,
}) => {
  const formRef = React.useRef<FormikProps<ArriveLoadFormValues>>(null);
  const formatLabel = (value?: string) => {
    if (!value) return "-";
    return value
      .toString()
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const typeLabel = React.useMemo(() => {
    if (freightType) {
      return formatLabel(freightType);
    }
    const origin = originCity?.toString().toUpperCase();
    const destination = destinationTerminalName?.toString().toUpperCase();
    if (!origin || !destination) return "-";
    return origin === destination ? "Local" : "Linehaul";
  }, [freightType, originCity, destinationTerminalName]);

  const isLocal = typeLabel.toLowerCase() === "local";
  const toIanaTimezone = (tz?: string) => {
    if (!tz) return undefined;
    const key = tz.toString().trim().toUpperCase();
    const map: Record<string, string> = {
      PST: "America/Los_Angeles",
      PDT: "America/Los_Angeles",
      MST: "America/Denver",
      MDT: "America/Denver",
      CST: "America/Chicago",
      CDT: "America/Chicago",
      EST: "America/New_York",
      EDT: "America/New_York",
    };
    return map[key] || undefined;
  };
  const destinationIanaTimezone = toIanaTimezone(destinationTerminalTimezone);
  const localMaxDate = isLocal
    ? (destinationIanaTimezone
        ? moment().tz(destinationIanaTimezone).toDate()
        : moment().toDate())
    : undefined;

  const sealConditionOptions = [
    { key: "intact", label: "Yes, seal intact" },
    { key: "damaged", label: "No, seal damaged" },
  ];

  const reasonForDamagedSealOptions: XGSSelectOption[] = [
    { label: "Previous Stop Did Not Reseal (Not Reseal)", value: "not_reseal" },
    { label: "Driver Broke Seal (Driver)", value: "driver" },
    { label: "Seal Tampered With (Tampered)", value: "tampered" },
    { label: "Seal Not on Trailer (Missing)", value: "missing" },
  ];

  const formatTime = (time: string): string => {
    // Remove any non-digit characters
    const digits = time.replace(/\D/g, "");
    if (digits.length === 0) return "";
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}:${digits.slice(2)}`;
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  };

  // Reset form when drawer opens to ensure clean state
  useEffect(() => {
    if (isOpen && formRef.current) {
      formRef.current.resetForm({ values: initialValues });
      const now = destinationIanaTimezone
        ? moment().tz(destinationIanaTimezone)
        : moment();
      formRef.current.setFieldValue("arrivalDate", now.format("MM/DD/YYYY"));
      formRef.current.setFieldValue("arrivalTime", now.format("HH:mm"));
    }
  }, [isOpen, destinationIanaTimezone]);

  return (
    <SlideOutSidebar
      header="Arrive Load"
      show={isOpen}
      size="small"
      onClose={onClose}
    >
      <div className="arrive-load-modal">
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => {
            onConfirm(values, isLocal);
          }}
          innerRef={formRef}
          enableReinitialize={false}
        >
          {(props: FormikProps<ArriveLoadFormValues>) => (
            <Form>
              <div className="arrive-load-modal__info">
                {/* LABEL ROW */}
                <div className="arrive-load-modal__info-row labels">
                  <div>Manifest</div>
                  <div>Trailer</div>
                  <div>Origin</div>
                </div>

                {/* VALUE ROW */}
                <div className="arrive-load-modal__info-row values">
                  <div>MF-{manifestNumber || "-"}</div>
                  <div>{trailerNumber ? `TRL-${trailerNumber}` : "-"}</div>
                  <div>{originTerminal || "-"}</div>
                </div>

                <div className="arrive-load-modal__info-row labels arrive-load-modal__info-row--type">
                  <div>Type</div>
                </div>
                <div className="arrive-load-modal__info-row values arrive-load-modal__info-row--type">
                  <div>{typeLabel}</div>
                </div>
              </div>

              <div className="arrive-load-modal__form">
                <div className="arrive-load-modal__row">
                  <LabeledDateInput
                    label="Arrival Date"
                    labelMode={LabelModes.column}
                    name="arrivalDate"
                    value={props.values.arrivalDate}
                    onDateChange={(date) => {
                      props.setFieldValue(
                        "arrivalDate",
                        date ? moment(date).format("MM/DD/YYYY") : ""
                      );
                    }}
                    placeholder="MM/DD/YYYY"
                    required
                    requiredAsteriskDisabled={false}
                    className="arrive-load-modal__input"
                    minDate={moment().subtract(1, "year").toDate()}
                    maxDate={localMaxDate}
                  />

                  <LabeledTextInput
                    label="Arrival Time"
                    labelMode={LabelModes.column}
                    name="arrivalTime"
                    value={props.values.arrivalTime}
                    onChange={(e) => {
                      const formatted = formatTime(e.target.value);
                      props.setFieldValue("arrivalTime", formatted);
                    }}
                    placeholder="--:--"
                    required
                    requiredAsteriskDisabled={false}
                    className="arrive-load-modal__input"
                    maxLength={5}
                  />
                </div>

                {!isLocal && (
                  <>
                    <div className="arrive-load-modal__row">
                      <LabeledTextInput
                        label="Seal Number"
                        labelMode={LabelModes.column}
                        name="seal"
                        value={props.values.seal}
                        onChange={(e) => {
                          props.setFieldValue("seal", e.target.value);
                        }}
                        placeholder="Enter Seal Number"
                        required
                        requiredAsteriskDisabled={false}
                        className="arrive-load-modal__input"
                      />

                      <LabeledTextInput
                        label="Checked By"
                        labelMode={LabelModes.column}
                        name="checkedBy"
                        value={props.values.checkedBy}
                        onChange={(e) => {
                          props.setFieldValue("checkedBy", e.target.value);
                        }}
                        placeholder="Employee Name"
                        required
                        requiredAsteriskDisabled={false}
                        className="arrive-load-modal__input"
                      />
                    </div>

                    <div className="arrive-load-modal__field">
                      <LabeledInput
                        label="Seal Condition"
                        labelMode={LabelModes.column}
                        required
                        requiredAsteriskDisabled={false}
                      >
                        <XGSRadio
                          name="sealCondition"
                          value={props.values.sealCondition}
                          options={sealConditionOptions}
                          onChange={(value) => {
                            props.setFieldValue("sealCondition", value);
                            if (value !== "damaged") {
                              props.setFieldValue("reasonForDamagedSeal", "");
                            }
                          }}
                        />
                      </LabeledInput>
                    </div>

                    {props.values.sealCondition === "damaged" && (
                      <LabeledSelectInput
                        label="Reason for Damaged Seal"
                        labelMode={LabelModes.column}
                        value={
                          props.values.reasonForDamagedSeal
                            ? reasonForDamagedSealOptions.find(
                                (opt) => opt.value === props.values.reasonForDamagedSeal
                              ) || null
                            : null
                        }
                        onValueChange={(v) => {
                          props.setFieldValue("reasonForDamagedSeal", v?.value || "");
                        }}
                        options={reasonForDamagedSealOptions}
                        placeholder="Select a reason"
                        required
                        className="arrive-load-modal__input"
                        menuPlacement="bottom"
                      />
                    )}
                  </>
                )}
              </div>

              <div className="arrive-load-modal__buttons">
                <Button
                  type="button"
                  theme={ButtonThemes.gray}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  theme={ButtonThemes.blue}
                  disabled={
                    !props.values.arrivalDate ||
                    !props.values.arrivalTime ||
                    (!isLocal && (
                      !props.values.seal ||
                      !props.values.checkedBy ||
                      !props.values.sealCondition ||
                      (props.values.sealCondition === "damaged" && !props.values.reasonForDamagedSeal)
                    ))
                  }
                >
                  Confirm
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </SlideOutSidebar>
  );
};

export default ArriveLoadModal;

